/**
 * Protected Route Component
 * Guards routes that require authentication
 *
 * Features:
 * - Redirects unauthenticated users to login
 * - Shows loading state during auth initialization
 * - Preserves intended destination for redirect after login
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { PageLoader } from '@/components/common/PageLoader';
import { PUBLIC_ROUTES } from './routes';

export interface ProtectedRouteProps {
  /** Child components to render when authenticated */
  children: React.ReactNode;
  /** Custom redirect path (defaults to login) */
  redirectTo?: string;
}

/**
 * ProtectedRoute - Guards routes requiring authentication
 *
 * Shows a loading state while auth is initializing, then either:
 * - Renders children if user is authenticated
 * - Redirects to login if user is not authenticated
 *
 * Preserves the attempted location so user can be redirected
 * back after successful login.
 *
 * @example
 * ```tsx
 * <ProtectedRoute>
 *   <DashboardPage />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = PUBLIC_ROUTES.LOGIN,
}) => {
  const { isAuthenticated, isInitialized, isLoading, user } = useAuthStore();
  const location = useLocation();

  // Show loading state while auth is initializing
  if (!isInitialized || isLoading) {
    return <PageLoader />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Preserve the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (user?.mustChangePassword && location.pathname !== PUBLIC_ROUTES.CHANGE_PASSWORD) {
    return <Navigate to={PUBLIC_ROUTES.CHANGE_PASSWORD} replace />;
  }

  // Render protected content
  return <>{children}</>;
};
